import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PhoneIcon from '@mui/icons-material/Phone'
import { useTranslation } from 'react-i18next'

import { galleryApi } from '../../api/endpoints'
import { Lightbox } from '../../components/common/Lightbox'

/**
 * Lena and Oleg's number, in the two forms it has to exist in.
 *
 * Kept together in one place so they cannot drift: the spaced version is what a person reads,
 * the E.164 version is what wa.me and tel: need, and a mismatch between them is a phone call
 * that never connects.
 */
const PHONE_DISPLAY = '+358 44 092 26 51'
const PHONE_E164 = '358440922651'

/**
 * Who runs the place, in their own words.
 *
 * <p>Twenty years is the single most persuasive fact about this estate and it appeared nowhere
 * on the site. The text is Lena's, translated rather than rewritten - the shape of it, the
 * short lines and the thanks to people who keep coming back, is the voice guests already know
 * from her Airbnb messages.
 *
 * <p>The photographs come from the library by subject rather than being listed here, so when
 * Lena's pictures are added they appear without a deploy. The section renders perfectly well
 * with none: an empty strip is better than a stock photograph of somebody else.
 */
export function OwnersIntro() {
  const { t } = useTranslation()

  const portraits = useQuery({
    queryKey: ['gallery', 'owners'],
    queryFn: () => galleryApi.page('OWNER', 0, 6),
  })

  const photos = portraits.data?.items ?? []
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const move = (delta: number) =>
    setOpenIndex((current) => {
      if (current === null) return current
      const next = current + delta
      return next < 0 || next >= photos.length ? current : next
    })

  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 5 } }}>
        <Typography variant="overline" color="primary" sx={{ letterSpacing: 1.5 }}>
          {t('owners.eyebrow')}
        </Typography>

        <Typography variant="h2" sx={{ mt: 1, mb: 3 }}>
          ❤️ {t('owners.heading')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 3, md: 5 },
            gridTemplateColumns: { xs: '1fr', md: photos.length > 0 ? '1.4fr 1fr' : '1fr' },
            alignItems: 'start',
          }}
        >
          <Box>
            <Stack spacing={2} sx={{ maxWidth: 680 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
                {t('owners.p1')}
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
                {t('owners.p2')} 🥰
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
                {t('owners.p3')}
              </Typography>
              <Typography variant="h5" component="p" sx={{ fontWeight: 400, pt: 1 }}>
                🌿 {t('owners.closing')}
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
                {t('owners.invite')}
              </Typography>
            </Stack>

            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t('owners.regards')}
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.5 }}>
                {t('owners.lena')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('owners.lenaAndOleg')}
              </Typography>

              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  href={'https://wa.me/' + PHONE_E164}
                  target="_blank"
                  rel="noopener"
                  sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' } }}
                >
                  {t('owners.whatsapp')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  href={'tel:+' + PHONE_E164}
                >
                  {PHONE_DISPLAY}
                </Button>
              </Stack>
            </Box>
          </Box>

          {photos.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                // One large picture reads as a portrait; two or more read as a family. The
                // grid changes shape rather than cropping everything into the same square.
                gridTemplateColumns: photos.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                gap: 1.5,
              }}
            >
              {photos.map((photo, index) => {
                // The first one gets both columns when there is an odd number, so the strip
                // never ends on a lonely half-width square.
                const wide = photos.length > 1 && photos.length % 2 === 1 && index === 0
                return (
                  // A button, not a clickable div: it opens full size from the keyboard too,
                  // the same lightbox every other photo grid on the site uses.
                  <ButtonBase
                    key={photo.url}
                    onClick={() => setOpenIndex(index)}
                    aria-label={t('owners.openPhoto')}
                    focusRipple
                    sx={{
                      display: 'block',
                      width: '100%',
                      borderRadius: 3,
                      overflow: 'hidden',
                      cursor: 'zoom-in',
                      gridColumn: wide ? 'span 2' : undefined,
                      '&:hover img': { transform: 'scale(1.04)' },
                      '&.Mui-focusVisible': { outline: 3, outlineColor: 'primary.main' },
                    }}
                  >
                    <Box
                      component="img"
                      // The small copy for the half-width squares; the wide one is twice the
                      // size on screen, so it keeps the original. The lightbox always opens
                      // the original.
                      src={wide || photos.length === 1 ? photo.url : (photo.thumbnailUrl ?? photo.url)}
                      alt={photo.altText ?? t('owners.photoAlt')}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: '100%',
                        aspectRatio: photos.length === 1 ? '4 / 3' : '1 / 1',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 400ms ease',
                      }}
                    />
                  </ButtonBase>
                )
              })}
            </Box>
          )}
        </Box>
      </CardContent>

      <Lightbox
        items={photos}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onMove={move}
      />
    </Card>
  )
}
